/**
 * Адреса и загрузка: склейки поставщика с настройками шестерёнки листания
 * (`ringParams`), путь ленты склейки, адрес раздела «Товары», поставщик из
 * адреса и склейка в CRM.
 */

import type { WindowValue } from "@/lib/reorder";
import { apiFetch, query } from "@/lib/api";
import { type NavSettings, ringParams } from "@/lib/productNav";
import { SEARCH_PARAM } from "@/lib/productSearch";
import type { Models, StripSettings } from "@/lib/similar/types";

/** Склейки поставщика либо всех — настройками шестерёнки листания.
 *
 * НАБОР ПЛИТКИ ДОБАВЛЯЕТСЯ ЗДЕСЬ, а не в общих `ringParams`: у стрелок карточки
 * плиток нет, и «прибыль» в наборе удорожала бы каждое их кольцо на секунду. */
export function loadModels(
  supplierId: number | null,
  s: NavSettings,
  w: WindowValue,
  sd: number,
): Promise<Models> {
  const адрес = supplierId === null ? "/api/models" : `/api/supplier/${supplierId}`;
  const show = s.show.join(",");
  // ГОРИЗОНТ ДОВОЗА ДОБАВЛЯЕТСЯ ЗДЕСЬ, как и набор плитки: у стрелок карточки
  // показателя «довести» нет, и слать им параметр незачем.
  // Дата первой поставки режет набор на сервере и стрелкам карточки не нужна —
  // добавляется здесь, как набор плитки и горизонт довоза. Теги — так же; `tagop=or`
  // сервер знает как умолчание и не получает.
  return apiFetch<Models>(
    адрес +
      query({
        ...ringParams(s, w),
        show: show || undefined,
        sd,
        fs: s.fs ?? undefined,
        tag: s.tag ?? undefined,
        tagop: s.tag !== null && s.tagop === "and" ? "and" : undefined,
      }),
  );
}

/** Путь ленты склейки: своё окно СТАТИСТИКИ и окно УХОДИМОСТИ КАРТОЧКИ (`w`).
 *
 * Второе — не настройка ленты: «к заказу» под фото обязано совпадать со строкой
 * таблицы размеров этажом ниже, и правит его переключатель карточки (16.09.26,
 * замечание владельца «не нужно смешивать период и период уходимости»).
 *
 * ПУТЬ, А НЕ ЗАГРУЗЧИК (23.09.26): ответ кладёт в кэш `productData.cachedFetch`,
 * и тем же путём грузится лента СОСЕДНЕГО товара заранее. Зови загрузку этот
 * модуль сам, `similar.ts` пришлось бы импортировать кэш, который уже
 * импортирует его, — вышло бы кольцо. */
export function similarPath(productId: number, s: StripSettings, w: number): string {
  return (
    `/api/product/${productId}/similar` +
    query({ from: s.from, to: s.to, show: s.show.join(",") || undefined, w })
  );
}

/** Адрес раздела «Товары»: все поставщики либо один, склейка, окно уходимости и
 *  поиск — он переживает смену поставщика и разворот склейки. */
export function productsHref(
  supplierId: number | null,
  groupId?: number | null,
  w?: string | null,
  q?: string | null,
  sd?: string | null,
  zd?: string | null,
): string {
  const params = new URLSearchParams();
  if (groupId) params.set("group", String(groupId));
  if (w) params.set("w", w);
  if (q) params.set(SEARCH_PARAM, q);
  // Горизонт довоза и окно полки переживают смену поставщика тем же правилом,
  // что окно уходимости: это параметры расчёта, а не отбора набора.
  if (sd) params.set("sd", sd);
  if (zd) params.set("zd", zd);
  return productsPath(supplierId, params);
}

/**
 * Строка запроса «Товаров»: запятая и двоеточие в значении законны, и `toString`
 * кодирует их зря. ЭТО НЕ ВКУС: адрес `?ids=` на 1000 номеров — 7 КБ при пределе
 * nginx 8 КБ, а с `%2C` он стал бы 9 КБ (спека
 * `docs/specs/2026-10-30-products-url-filters-design.md`, раздел 3). Настоящий
 * `%` в значении приходит как `%25` и под замену не попадает.
 */
export function compactQuery(params: URLSearchParams): string {
  return params.toString().replaceAll("%2C", ",").replaceAll("%3A", ":");
}

/**
 * «Товары» с поставщиком ПАРАМЕТРОМ `?supplier=` (владелец 25.09.26): адрес
 * `/supplier/{id}` отдан под карточку поставщика — объекта, а не отбора товаров.
 * Поставщик стоит первым: по адресу сразу видно, чей набор.
 */
export function productsPath(supplierId: number | null, params: URLSearchParams): string {
  const rest = new URLSearchParams(params);
  rest.delete(SUPPLIER_PARAM);
  const head = supplierId === null ? "" : `${SUPPLIER_PARAM}=${supplierId}`;
  const хвост = [head, compactQuery(rest)].filter(Boolean).join("&");
  return `/products${хвост ? `?${хвост}` : ""}`;
}

/** Параметр поставщика у «Товаров» — то же имя, что у «Оплат». */
export const SUPPLIER_PARAM = "supplier";

/**
 * Поставщик из адреса «Товаров»: `null` — все поставщики, `"bad"` — параметр
 * есть, но это не номер (экран говорит «адрес неверен», а не молча все).
 */
export function supplierOf(params: URLSearchParams): number | null | "bad" {
  const raw = params.get(SUPPLIER_PARAM);
  if (raw === null) return null;
  return /^\d+$/.test(raw) && Number(raw) > 0 ? Number(raw) : "bad";
}

/** Страница поставщика; с группой — сразу товары этой модели. */
export function supplierHref(supplierId: number, groupId?: number | null): string {
  return productsHref(supplierId, groupId);
}

/** Модели склейки в CRM — тот же адрес, что у строки «склейка» карточки. */
export function crmSimilarHref(groupId: number): string {
  return `https://a.unirenter.ru/b24/r.php?page=product&do=modelList&modelSimilarGroupID=${groupId}`;
}
